import React, { useState, useMemo, useEffect, setEditQ, setEditIdx } from 'react';
import ReactDOM from 'react-dom';
import { Table } from 'antd';
import { PostCallWithErrorResponse } from '../Api/ApiServices';
import { ApiConfig } from '../Api/ApiConfig';
import { toastMsg } from '../component/toast';
import { difficultyLevel } from '../constant/dificultyLevel';
import { retry } from '@reduxjs/toolkit/query';
import { Edit, Delete } from 'lucide-react';
import { SquarePen, Trash, User, Replace } from 'lucide-react';
import AiGenerator from './generateQuestionFromAi';

/* ==============================================================================================
   1. DESIGN SYSTEM & ASSETS (Merged from master (2).css & lywo-design-system.css)
   ============================================================================================== */

const LYWO_CSS = `
@charset "UTF-8";
@import url("https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css");
@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");

:root {
  /* --- Brand Colors --- */
  --brand-primary: #0d6efd;
  --brand-bg: #f5f8ff;
  --brand-text: #212529;
  
  /* --- Semantic Colors --- */
  --color-info-bg: #eef2ff;
  --color-success-bg: #d1e7dd; --color-success-text: #198754;
  --color-warning-bg: #fff3cd; --color-warning-text: #856404;
  --color-danger-bg: #f8d7da; --color-danger-text: #dc3545;
  
  /* --- UI Neutrals --- */
  --ui-gray-bg: #F2F4F7;
  --ui-border: #d0d5dd;
  --ui-text-muted: #667085;

  /* --- Dimensions --- */
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --font-base: 14px;
  
  /* --- Lywo Specifics (Mapped) --- */
  --lywo-primary: var(--brand-primary);
  --lywo-bg: var(--brand-bg);
  --lywo-text: var(--brand-text);
  --lywo-border-light: var(--ui-border);
  --lywo-radius: var(--radius-md);
}

body { 
  margin: 0; padding: 0; 
  font-family: 'Inter', sans-serif !important; 
  font-size: var(--font-base); 
  background-color: var(--ui-gray-bg); 
  -webkit-font-smoothing: antialiased; 
  overflow: hidden;
}

/* Layout Utilities */
.app-container { height: 100vh; display: flex; flex-direction: column; background: var(--brand-bg); }
.app-header { background: #fff; padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--ui-border); display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; z-index: 100; }
.app-content { flex: 1; overflow: scroll;padding-bottom:150px !important; display: flex; flex-direction: column; padding: 1.5rem; width: 100%; box-sizing: border-box; }

/* Content Area */
.content-area {
  background: #fff;
  border-radius: var(--radius-md);
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  border: 1px solid var(--ui-border);

  display: flex;
  flex-direction: column;

  height: 50vh;                  /* keep if needed */
  width: 100%;

  overflow-y: auto;              /* ✅ vertical only */
  overflow-x: hidden;

  padding-bottom: 120px;         /* 🔥 space for footer */
}


.panel-header { 
  padding: 0.75rem 1.5rem; 
  border-bottom: 1px solid var(--ui-border); 
  background: #fff; 
  flex-shrink: 0; 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  min-height: 60px; 
}

.panel-body { 

gap:0px;
  padding: 1.5rem; 
  overflow-y: auto; 
  flex: 1; 
  width: 100%; 
  background: #f8f9fa;
}

/* Sticky Footer */
.sticky-action-bar { 
  padding: 1rem 1.5rem; 
  border-top: 1px solid var(--ui-border); 
  background: #fff; 
  flex-shrink: 0; 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  z-index: 10;
}

/* Components: Buttons */
.btn { font-weight: 600; padding: 0.6rem 1.2rem; border-radius: var(--radius-md); display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; transition: 0.2s; font-size: 14px; border: 1px solid transparent; cursor: pointer; }
.btn-primary { background-color: var(--brand-primary); border-color: var(--brand-primary); color: white; }
.btn-primary { background-color: #0b5ed7; transform: translateY(-1px); opacity: 0.95; }
.btn-outline-primary { background: #fff; color: var(--ui-text-muted); border-color: var(--ui-border); }
.btn-outline-primary:hover { border-color: var(--brand-primary); color: var(--brand-primary); background: var(--color-info-bg); }
.btn-danger-ghost { color: var(--color-danger-text); background: transparent; border: 1px solid transparent; }
.btn-danger-ghost:hover { background: #fee2e2; border-color: #f5c6cb; }
.btn-icon { padding: 0.4rem; color: var(--ui-text-muted); background: transparent; border: none; border-radius: var(--radius-md); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
.btn-icon:hover { background-color: var(--ui-gray-bg); color: var(--brand-primary); }

/* Components: Cards */
.dashboard-card { background: #fff; border: 1px solid var(--ui-border); border-radius: 12px; padding: 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); height: 100%; display: flex; flex-direction: column; transition: 0.2s; }
// .dashboard-card:hover { border-color: var(--brand-primary); transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
.card-accent-left { border-left: 4px solid var(--brand-primary); }

.selectable-option { border: 2px solid var(--ui-border); border-radius: 12px; padding: 2rem; text-align: center; cursor: pointer; transition: all 0.2s; background: white; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; }
.selectable-option:hover { border-color: var(--brand-primary); background-color: #f8faff; transform: translateY(-4px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
.icon-circle { width: 64px; height: 64px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem; font-size: 1.75rem; }

/* Navigation */
.nav-btn { background: transparent; color: var(--ui-text-muted); font-weight: 600; padding: 0.5rem 0.75rem; border: none; border-radius: 0.5rem; transition: all 0.2s; cursor: pointer; }
.nav-btn:hover { color: var(--brand-primary); background: var(--brand-bg); }
.nav-btn.active { color: var(--brand-primary); background: #e0e7ff; }

/* Badges */
.badge { padding: 0.35em 0.65em; border-radius: 6px; font-size: 0.75em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.badge-primary { background: #e0e7ff; color: var(--brand-primary); }
.badge-success { background: var(--color-success-bg); color: var(--color-success-text); }
.badge-warning { background: var(--color-warning-bg); color: var(--color-warning-text); }
.badge-info { background: var(--color-info-bg); color: var(--brand-primary); }
.badge-secondary { background: #e2e8f0; color: #475569; }

/* Forms */
.form-control { width: 100%; padding: 0.6rem; border: 1px solid var(--ui-border); border-radius: 0.5rem; font-size: 0.9rem; background: #fff; color: #333; }
.form-control:focus { outline: none; border-color: var(--brand-primary); box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1); }
.form-label { font-size: 13px; font-weight: 700; color: #344054; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }

/* Visual Helpers */
.rationale-box { background-color: var(--color-warning-bg); color: var(--color-warning-text); border: 1px solid #ffeeba; padding: 0.75rem; border-radius: 0.5rem; font-size: 0.85rem; margin-top: 0.5rem; }
.text-truncate-cell { max-width: 250px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
.anim-fade { animation: fadeIn 0.3s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }

/* Table Overrides */
.table { --bs-table-bg: transparent; --bs-table-color: var(--brand-text); --bs-table-border-color: var(--ui-border); width: 100%; margin-bottom: 0; vertical-align: middle; }
.table > thead { background-color: #F9FAFB; }
.table > thead > tr > th { padding: 1rem; font-weight: 600; color: var(--ui-text-muted); border-bottom: 1px solid var(--ui-border); font-size: 13px; text-transform: uppercase; text-align: left; }
.table > tbody > tr > td { padding: 1rem; border-bottom: 1px solid var(--ui-border); color: var(--brand-text); font-size: 14px; vertical-align: middle; }
.table-hover > tbody > tr:hover { background-color: var(--color-info-bg); }

/* Modal Fixes */
.custom-modal-overlay { position: fixed; inset: 0; background-color: rgba(0,0,0,0.5); z-index: 1050; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.2s ease-out; }
.custom-modal-dialog { background: #fff; width: 100%; max-height: 100vh; border-radius: var(--radius-lg); box-shadow: 0 10px 25px rgba(0,0,0,0.1); display: flex; flex-direction: column; overflow: hidden; animation: slideUp 0.3s ease-out; }
.custom-modal-header { padding: 1rem 1.5rem; border-bottom: 1px solid var(--ui-border); display: flex; justify-content: space-between; align-items: center; background: #fff; }
.custom-modal-body { padding: 1.5rem;  flex: 1; background: #fff; }
.custom-modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--ui-border); background: #f8f9fa; display: flex; justify-content: flex-end; gap: 1rem; }
`;

const Icons = {
  Edit: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  Trash: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  Play: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3" /></svg>,
  Chart: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>,
  Sparkles: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L12 3Z" /><path d="M5 3v4" /><path d="M9 5H3" /><path d="M19 19v2" /><path d="M21 20h-2" /></svg>,
  Grid: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>,
  List: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
  Upload: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
  Check: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>,
  Refresh: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
  More: (p) => <svg
    {...p}
    width="1.3em"
    height="1.3em"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="19" r="1" />
  </svg>
  ,
  Close: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Download: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
  File: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>,
  Plus: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  Copy: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>,
  Replace: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
  Regenerate: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>,
  User: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
  Brain: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" /><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" /></svg>
};

/* ==============================================================================================
   2. MOCK SERVICES & DATA
   ============================================================================================== */

const JOB_DESCRIPTIONS = [
  {
    title: "Frontend Developer",
    subTopics: ["React", "CSS Grid", "JavaScript ES6", "Accessibility", "Performance Optimization", "Redux", "TypeScript", "Jest", "Webpack", "HTML5 Semantics"]
  },
  {
    title: "Backend Engineer",
    subTopics: ["Node.js", "Express", "MongoDB", "SQL Optimization", "REST APIs", "GraphQL", "Microservices", "Docker", "AWS Lambda", "Redis"]
  },
  {
    title: "DevOps Specialist",
    subTopics: ["CI/CD Pipelines", "Kubernetes", "Terraform", "Linux Administration", "Bash Scripting", "Azure DevOps", "Monitoring (Prometheus)", "Security Best Practices"]
  }
];

const DURATION_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 45, 60];

/* --- START: JOB DESCRIPTION DATA (Copy for Live Project) --- */
const MOCK_JDS = [
  { id: 1, title: 'Senior Frontend Developer (React)', topics: ['Frontend', 'React', 'JavaScript', 'CSS', 'Web Performance'] },
  { id: 2, title: 'Backend Engineer (Node.js)', topics: ['Backend', 'Node.js', 'API Design', 'Database', 'Security'] },
  { id: 3, title: 'DevOps Engineer', topics: ['DevOps', 'AWS', 'Docker', 'Kubernetes', 'CI/CD'] },
  { id: 4, title: 'UI/UX Designer', topics: ['Design', 'Figma', 'Prototyping', 'User Research', 'Accessibility'] },
  { id: 5, title: 'Full Stack Developer', topics: ['Frontend', 'Backend', 'Database', 'API Design', 'Deployment'] }
];
/* --- END: JOB DESCRIPTION DATA --- */


const MockService = {
  simulateAi: (type, payload) => new Promise(resolve => {
    setTimeout(() => {
      const qTemplate = (i, topic) => ({
        id: crypto.randomUUID(),
        text: `AI Question ${i + 1} regarding ${topic}`,
        type: 'MCQ',
        weight: 1,
        rationale: "AI generated rationale explaining the correct answer.",
        options: [{ id: crypto.randomUUID(), text: 'Option A' }, { id: crypto.randomUUID(), text: 'Option B' }, { id: crypto.randomUUID(), text: 'Option C' }, { id: crypto.randomUUID(), text: 'Option D' }],
        correctOptionId: '',
        topics: ['AI', topic]
      });
      const fixCorrectId = (q) => { q.correctOptionId = q.options[0].id; return q; };
      if (type === 'GET') resolve({ questions: Array(parseInt(payload.count)).fill(0).map((_, i) => fixCorrectId(qTemplate(i, payload.topic))) });
      if (type === 'REPLACE') resolve({ question: fixCorrectId(qTemplate(99, 'Replacement')) });
      if (type === 'REGENERATE') resolve({ question: fixCorrectId({ ...qTemplate(99, 'Regen'), text: `Regenerated: ${payload.feedback}` }) });
    }, 800);
  })
};

/* ==============================================================================================
   3. SHARED COMPONENTS (FIXED MODAL)
   ============================================================================================== */

const Modal = ({ title, onClose, children, footer, size = '950px',heightSize='650px' }) => (
  <div className="custom-modal-overlay">
    <div className="custom-modal-dialog" style={{ maxWidth: size,height:heightSize  }}>
      <div className="">
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{title}</h3>
        {/* <button onClick={onClose} style={{background:'none', border:'none', cursor:'pointer'}} title="Close"></></button> */}
      </div>
      <div className="custom-modal-body">{children}</div>
      {footer && <div className="custom-modal-footer">{footer}</div>}
    </div>
  </div>
);

const Toast = ({ msg }) => (
  <div className="anim-fade" style={{ position: 'fixed', bottom: 20, right: 20, background: '#212529', color: '#fff', padding: '10px 20px', borderRadius: '8px', zIndex: 1200, fontSize: '0.9rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
    {msg}
  </div>
);

const UserBadge = ({ user }) => {
  return <span className="badge badge-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }} title={`Updated by: ${user}`}><Icons.User style={{ fontSize: '0.9em' }} /> {user}</span>;
};

// --- Shared Navigation Header ---
const ViewNavigation = ({ view, setView, onCreate }) => (
  <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px' }}>
    <div className="nav-tabs-horizontal">
      <button onClick={() => setView('dashboard')} className={`nav-btn-h ${view === 'dashboard' ? 'active' : ''}`}>Available</button>
      <button onClick={() => setView('active')} className={`nav-btn-h ${view === 'active' ? 'active' : ''}`}>Deployed</button>
    </div>
    <div style={{ flexGrow: 1 }}></div>
    <button
      onClick={onCreate}
      className="btn-primary"
      title="Create a new quiz"
      style={{ margin: '0 10px 0 0' }}
    >
      New Quiz
    </button>
  </div>
)

const dropdownItemStyle = {
  display: 'flex', alignItems: 'center', gap: '8px',
  padding: '8px 12px', width: '100%', border: 'none',
  background: 'transparent', textAlign: 'left',
  fontSize: '14px', color: 'var(--brand-text)',
  cursor: 'pointer', transition: 'background 0.2s'
};

/* ==============================================================================================
   4. FEATURE VIEWS
   ============================================================================================== */

// --- Dashboard (Drafts) ---
const DashboardView = ({ assessments, onEdit, onDelete, view, setView, onNew }) => {
  const [viewMode, setViewMode] = useState('list');

  // ✅ DELETE MODAL STATES (ADDED)
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Unified List: Show all Draft/Master, no repo filtering
  const data = assessments.filter(
    a => a.status === 'Draft' || a.status === 'Ready'
  );

  const columns = [
    {
      title: "Quiz Title",
      dataIndex: "title",
      key: "title",
      width: "40%",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#000" }}>
            {record.title}
          </span>
          <span className='text-base' style={{ color: "#999", fontSize: '11' }}>
            ({record.questions?.length} Qs • {record.durationMinutes} m)
          </span>
        </div>
      ),
    },
    {
      title: "User",
      dataIndex: "updatedBy",
      key: "updatedBy",
      width: "20%",
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "15%",
      render: (date) =>
        date ? new Date(date).toLocaleDateString() : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15%",

      render: (status) => (
        <div style={{ textAlign: 'left' }}>
          <span
            className={status === "Draft" ? "badge-warning" : "badge-info"}
            style={{
              fontSize: "8px",
              borderRadius: "10px",
              padding: "2px 12px",
              fontWeight: "bold",
              textTransform: "capitalize",
              display: "inline-block",
              textAlign: "left",
              margin: "0",
              marginLeft: "-10px",
            }}
          >
            {status.toLowerCase()}
          </span>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      align: "right",
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => onEdit(record)} title="Edit"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <SquarePen size={14} />
          </button>
          <button
            onClick={() => { setDeleteId(record.id); setShowDelete(true); }} title="Delete"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <Trash size={14} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="content-area" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="panel-header" style={{ padding: '15px 20px', background: '#fff', borderBottom: '1px solid #eee' }}>
        <ViewNavigation view={view} setView={setView} onCreate={onNew} />

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('grid')}
            title="Grid View"
          >
            <Icons.Grid />
          </button>
          <button
            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('list')}
            title="List View"
          >
            <Icons.List />
          </button>
        </div>
      </div>

      <div className="panel-body-custom" style={{
        background: '#f8f9fa',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '0'
      }}>
        {data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '10rem', color: '#aaa' }}>
            No assessments found.
          </div>
        ) : viewMode === 'grid' ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem',
              padding: '20px'
            }}
          >
            {data.map(a => (
              <div key={a.id} className="dashboard-card p-3">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span
                    style={{
                      fontSize: "11px",
                      borderRadius: "10px",
                      padding: "2px 12px",
                      fontWeight: "bold",
                      textTransform: "capitalize",
                      display: "inline-block",
                      textAlign: "left",
                      margin: "0",
                      height: '18px',
                      marginLeft: "-5px",
                    }}
                    className={`badge ${a.status === 'Draft' ? 'badge-warning' : 'badge-info'}`}
                  >
                    {a.status}
                  </span>

                  <div style={{ display: 'flex', gap: '0px' }}>
                    <button className="btn-icon" onClick={() => onEdit(a)} title="Edit">
                      <SquarePen size={14} />
                    </button>
                    <button className="btn-icon" onClick={() => { setDeleteId(a.id); setShowDelete(true); }} title="Delete">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>

                <h4 className='.text-lg' style={{ margin: '0 0 0.5rem 0', fontSize: '14px', fontWeight: 700 }}>{a.title}</h4>

                <div style={{ marginBottom: '0.5rem', fontSize: '11px' }}>
                  <span className=".text-base" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    backgroundColor: '#e9ecef',
                    padding: '2px 10px',
                    borderRadius: '6px',
                    color: '#495057',
                    fontWeight: '500'
                  }}>
                    <User size={14} />
                    {a.updatedBy?.name || a.updatedBy}
                  </span>
                </div>
                <div style={{ marginBottom: '0.5rem', fontSize: '11px' }}>
                  <span className=".text-base" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '2px 10px',
                    borderRadius: '6px',
                    color: '#495057',
                    fontWeight: '500'
                  }}>
                    Last updated: {new Date(a.updatedAt).toLocaleDateString()}
                  </span>
                </div>



                <div className=".text-base" style={{
                  fontSize: '11px',
                  color: 'var(--ui-text-muted)',
                  marginTop: 'auto',
                  borderTop: '1px solid var(--ui-border)',
                  paddingTop: '0.5rem'
                }}>
                  {a.questions.length} Qs • {a.durationMinutes} Minutes
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="assessment-table" style={{ paddingLeft: '18px', marginTop: '-10px', paddingRight: '18px' }}>
            <Table
              size="large"
              columns={columns}
              dataSource={data}
              // onChange={handleChange}
              // pagination={false}
              className="grouplisttable overFlowTable"
            // tableLayout="fixed"

            // sticky={{ offsetHeader: 0 }}
            // rowSelection={true}
            />
          </div>

          //        <table className="assessment-table" style={{ 
          //   width: '100%', 
          //   borderCollapse: 'collapse', 
          //   background: '#fff',
          //   fontSize: '12px' 
          // }}>
          //   <thead>
          //     <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
          //       <th style={{ padding: '5px 7px', paddingLeft: '35px', width: '40%' }}>Quiz Title</th>
          //       <th style={{ padding: '5px 7px', width: '20%' }}>User</th>
          //       <th style={{ padding: '5px 7px', width: '15%' }}>Last Updated</th>
          //       <th style={{ padding: '5px 7px', paddingLeft: '45px', width: '15%' }}>Status</th>     
          //       <th style={{ padding: '5px 7px', width: '10%', textAlign: 'right', paddingRight: '25px' }}>Actions</th>
          //     </tr>
          //   </thead>

          //   <tbody>
          //     {data.map(a => (
          //       <tr key={a.id} style={{ borderBottom: '1px solid #eee' }}>
          //         <td style={{ padding: '5px 7px', paddingLeft: '35px' }}> 
          //           <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          //             <span style={{ fontWeight: 700, color: '#000' }}>{a.title}</span>
          //             <span style={{ color: '#999', fontSize: '12px' }}>
          //               ({a.questions.length} Qs • {a.durationMinutes} )
          //             </span>
          //           </div>
          //         </td>
          //         <td style={{ padding: '5px 7px' }}>{a.updatedBy}</td>
          //         <td style={{ padding: '5px 7px' }}>{new Date(a.updatedAt).toLocaleDateString()}</td>
          //         <td style={{ padding: '5px 7px', paddingLeft: '40px' }}>
          //           <span className={`badge ${a.status === 'Draft' ? 'badge-warning' : 'badge-info'}`} style={{ fontSize: '11px', fontWeight: 'bold' }}>
          //             {a.status.toUpperCase()}
          //           </span>
          //         </td>

          //         <td style={{ padding: '5px 7px', textAlign: 'right', paddingRight: '25px' }}>
          //           <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          //             <button 
          //               onClick={() => onEdit(a)} title="Edit"
          //               style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          //             >
          //               <SquarePen size={14}/>
          //             </button>
          //             <button 
          //               onClick={() => { setDeleteId(a.id); setShowDelete(true); }} title="Delete"
          //               style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          //             >
          //               <Trash size={14} />
          //             </button>
          //           </div>
          //         </td>
          //       </tr>
          //     ))}
          //   </tbody>
          // </table>
        )}
      </div>


      {/* CONFIRM DELETE MODAL */}
      {showDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: '12px', width: '400px', padding: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 600, marginBottom: '12px' }}>admin.ui.lywo.in says</div>
            <div style={{ marginBottom: '20px' }}>Delete?</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn btn-primary" style={{ height: '40px' }} onClick={() => { onDelete(deleteId); setShowDelete(false); setDeleteId(null); }}>OK</button>
              <button className="btn btn-light" style={{ width: '85px', height: '40px', border: '1px solid #000', color: '#000' }} onClick={() => { setShowDelete(false); setDeleteId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Deployed (Active) ---
const DeployedView = ({ assessments, submissions, onAction, onDelete, view, setView, onNew }) => {
  const [viewMode, setViewMode] = useState('list');
  const active = assessments.filter(a => a.status === 'Deployed');
  const columns = useMemo(() => [
    {
      title: 'Quiz Title',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      render: text => (
        <span style={{ color: '#000' }}>{text}</span>
      ),
    },
    {
      title: 'Company Name',
      dataIndex: 'groupCode',
      key: 'groupCode',
      width: 180,
    },
    {
      title: 'Position Name',
      dataIndex: 'position',
      key: 'position',
      width: 180,
      render: val => val || 'N/A',
    },
    {
      title: 'Candidates',
      key: 'candidates',
      width: 120,
      align: 'center',
      render: (_, record) => record.id || 0,
    },
    {
      title: 'Deployed Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 220,
      align: 'center',
      render: date => (
        <>
          {new Date(date).toLocaleDateString()} -
          {new Date(date).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </>
      ),
    },
    {
  title: 'Actions',
  key: 'actions',
  width: 10,
  align: 'center',
      render: (_, record) => (
       <div style={{ display: 'flex', gap: 0, justifyContent: 'center' }}>
  <button className="btn-icon" onClick={() => onAction(record, 'check')} title="Preview">
    <Icons.Play size={14} style={{ color: '#000' }} />
  </button>
  
  <button className="btn-icon" onClick={() => onAction(record, 'leaderboard')} title="Results">
    <Icons.Chart size={14} style={{ color: '#000' }} />
  </button>
  
  <button className="btn-icon" onClick={() => onAction(record, 'adjust')} title="Modify Point">
    <Icons.Edit size={14} style={{ color: '#000' }} />
  </button>
</div>
      ),
    },
  ], [onAction]);
  return (
    <div className="content-area" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="panel-header" style={{ padding: '15px 20px', background: '#fff', borderBottom: '1px solid #eee' }}>
        <ViewNavigation view={view} setView={setView} onCreate={onNew} />

        <div style={{ display: 'flex', gap: '8px' }}>
          <button className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setViewMode('grid')} title="Grid View"><Icons.Grid /></button>
          <button className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setViewMode('list')} title="List View"><Icons.List /></button>
        </div>
      </div>

      <div className="panel-body-custom" style={{
        background: '#f8f9fa',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '0'
      }}>
        {viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem', padding: '20px' }}>
            {active.map(a => (
              <div key={a.id} className="dashboard-card card-accent-left p-3">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <h4 className='text-lg' style={{ margin: 0, fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.title}>{a.title}</h4>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span className='text-base' style={{ background: '#eee', fontSize: '11px', fontWeight: 'bold', color: '#333', width: '160px' }}>Company: {a.groupCode}</span>

                    <span className='text-base' style={{ background: '#eee', fontSize: '11px', fontWeight: 'bold', color: '#333', width: '160px' }}>Designation:{a.position || 'N/A'}</span>
                  </div>
                  <div className='text-base' style={{ marginTop: '0.5rem', color: '#666', fontSize: '11px' }}>{submissions.filter(s => s.assessmentId === a.id).length} Candidates Attempted</div>
                  <div className='text-base' style={{ marginTop: '0.25rem', color: '#888', fontSize: '11px' }}>Deployed: {new Date(a.createdAt).toLocaleString()}</div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto' }}>
                  <button className="btn-primary" style={{ flex: 1, padding: '0.5rem' }} onClick={() => onAction(a, 'check')}>Preview</button>
                  <button className="btn-primary" style={{ flex: 1, padding: '0.5rem' }} onClick={() => onAction(a, 'leaderboard')}>Results</button>
                  <button className="btn-primary" style={{ flex: 1, padding: '0.5rem' }} onClick={() => onAction(a, 'adjust')}>Modify Points</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="assessment-table" style={{ paddingLeft: '18px', marginTop: '-10px', paddingRight: '18px' }}>
            <Table
              size="large"
              columns={columns}
              dataSource={active}
              // onChange={handleChange}
              // pagination={false}
              className="grouplisttable overFlowTable"
            // tableLayout="fixed"

            // sticky={{ offsetHeader: 0 }}
            // rowSelection={true}
            />
          </div>

          //         <table className="assessment-table" style={{ 
          //   width: '100%', 
          //   borderCollapse: 'collapse', 
          //   background: '#fff',
          //   fontSize: '12px' 
          // }}>
          //   <thead>
          //     <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
          //       <th style={{ padding: '5px 7px', paddingLeft: '35px', width: '25%' }}>Quiz Title</th>
          //       <th style={{ padding: '5px 7px', width: '15%' }}>Company Name</th>
          //       <th style={{ padding: '5px 7px', width: '15%' }}>Position Name</th>
          //       <th style={{ padding: '5px 7px', width: '10%', textAlign: 'center' }}>Candidates</th>
          //       <th style={{ padding: '5px 7px', width: '20%', textAlign: 'center' }}>Deployed Date & Time</th>

          //       <th style={{ padding: '5px 7px', width: '15%', textAlign: 'right', paddingRight: '35px' }}>Actions</th>
          //     </tr>
          //   </thead>
          //   <tbody>
          //     {active.map(a => (
          //       <tr key={a.id} style={{ borderBottom: '1px solid #eee' }}>
          //         <td style={{ padding: '5px 7px', paddingLeft: '35px', fontWeight: 700, color: '#000' }}>
          //           {a.title}
          //         </td>
          //         <td style={{ padding: '5px 7px' }}>{a.groupCode}</td>
          //         <td style={{ padding: '5px 7px' }}>{a.position || 'N/A'}</td>
          //         <td style={{ padding: '5px 7px', textAlign: 'center' }}>
          //           {submissions.filter(s => s.assessmentId === a.id).length}
          //         </td>
          //         <td style={{ padding: '5px 7px', textAlign: 'center' }}>
          //           <span style={{ color: '#333' }}>
          //             {new Date(a.createdAt).toLocaleDateString()} &nbsp; 
          //             {new Date(a.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          //           </span>
          //         </td>

          //         <td style={{ padding: '2px 7px', textAlign: 'right', paddingRight: '25px' }}>
          //           <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          //             <button onClick={() => onAction(a, 'check')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a49f9f', fontSize: '14px', padding: 0 }} title="Preview"><Icons.Play /></button>
          //             <button onClick={() => onAction(a, 'leaderboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a49f9f', fontSize: '14px', padding: 0 }} title="Results"><Icons.Chart /></button>
          //             <button onClick={() => onAction(a, 'adjust')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a49f9f', fontSize: '14px', padding: 0 }} title="Modify Points"><Icons.Edit /></button>
          //           </div>
          //         </td>
          //       </tr>
          //     ))}
          //   </tbody>
          // </table>
        )}
      </div>
    </div>
  );
};

// --- Editor (Create/Edit) ---
const EditorView = ({ assessment, onSave, onCancel }) => {
  const [local, setLocal] = useState(JSON.parse(JSON.stringify(assessment)));
  const [editQ, setEditQ] = useState(null);
  const [editIdx, setEditIdx] = useState(-1);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [difficulty, setDifficulty] = useState('Medium');

  const [isValided, setIsValided] = useState(false)

  const updateQ = (idx, field, val) => {
    const qs = [...local.questions]; qs[idx][field] = val;
    setLocal({ ...local, questions: qs });
  };



  const addQuestion = () => {
    const newId = crypto.randomUUID();
    const optId = crypto.randomUUID();
    const newQ = {
      text: 'New Question',
      type: 'MCQ',
      weight: 1,
      "q": "",
      "options": [
        "",
        "",
        "",
        ""
      ],
      "correct_answer": null,
      "rationale": ""
    }
    // setLocal(prev => ({ ...prev, questions: [...prev.questions, newQ] }));
    setEditQ(newQ);
    setEditIdx(local.questions.length);
  };

  const handleAiImport = (generatedQuestions) => {

    setLocal(prev => ({ ...prev, questions: [...prev.questions, ...generatedQuestions] }));
    setAiModalOpen(false);
  };

  const handleValidation = () => {

    let isValided = false

    if (!editQ?.q?.trim() || !editQ?.rationale?.trim()) {


      isValided = true
    }

    editQ?.options.forEach(opt => {

      if (!opt.trim()) {
        isValided = true
      }

    })

    if (!isValided && !editQ?.correct_answer) {

      toastMsg('error', 'Please select the correct answer.')

      isValided = true

    }



    setIsValided(isValided)
    return isValided

  }


  const saveModalChanges = () => {


    if (editIdx > -1 && editQ) {


      if (handleValidation()) return


      const qs = [...local.questions];

      qs[editIdx] = editQ;
      setLocal({ ...local, questions: qs });
    }
    setEditQ(null);
    setEditIdx(-1);
    setIsValided(false)
  };



  const handleQuestionUpdateFromAi = (type = "more like this", index = 0, question = '', topics = []) => {

    let payload = {
      difficulty_level: 'medium',
      job_description: '',
      question: question,
      topics: topics,
    }


    if (type == 'more like this') {
      payload.num_questions = 2
    }

    if (type == 'Replace') {
      payload.num_questions = 1
    }

    PostCallWithErrorResponse(ApiConfig.REPLACE_QUEST, payload).then((res) => {

      if (res?.json?.replaced_questions) {

        let quest = res?.json?.replaced_questions?.map((ques) => ({
          ...ques,
          text: 'New Question',
          type: 'MCQ',
          weight: 1,
        }))

        if (type == 'more like this') {

          setLocal(prev => ({
            ...prev,
            questions: [
              ...prev.questions.slice(0, index + 1),
              ...quest,
              ...prev.questions.slice(index + 1)
            ]
          }));

        }

        if (type == 'Replace') {

          const qs = [...local.questions];

          qs[index] = {
            ...quest[0]
          };
          setLocal({ ...local, questions: qs });

        }



      }

    })

  }


  return (
    <div className="content-area">
      <div
        className="panel-header"
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid #e5e7eb'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%'
          }}
        >
          {local.status !== 'Deployed' && (
            <div
              style={{
                textTransform: "capitalize",
                display: 'flex',
                alignItems: 'flex-end',
                gap: '16px',
                width: '100%'
              }}
            >
              {/* 1. Title Input */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label className='text-base'
                  htmlFor="quizTitle"
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#666',
                    textTransform: 'none',
                    marginBottom: '4px'
                  }}
                >
                  Title
                </label>
                <input
                  id="quizTitle"
                  className="form-control"
                  placeholder="Enter Quiz Name"
                  value={local.title}
                  onChange={e => {
                    const val = e.target.value;
                    const formattedTitle = val.length > 0 ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : '';
                    setLocal({ ...local, title: formattedTitle });
                  }}
                  style={{
                    height: '33px',
                    fontSize: '0.85rem',
                    width: '350px'
                  }}
                />
              </div>

              {/* 2. Duration Select */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label className='text-base'
                  htmlFor="quizDuration"
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#666',
                    textTransform: 'none',
                    marginBottom: '4px'
                  }}
                >
                  Duration (Minutes)
                </label>
                <select
                  id="quizDuration"
                  className="form-control"
                  value={local.durationMinutes}
                  onChange={e =>
                    setLocal({
                      ...local,
                      durationMinutes: parseInt(e.target.value) || 0
                    })
                  }
                  style={{
                    height: '32px',
                    fontSize: '0.85rem',
                    width: '200px',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    backgroundSize: '16px',
                    paddingRight: '30px',
                    backgroundColor: '#fff',
                    border: '1px solid #ced4da',
                    borderRadius: '8px'
                  }}
                >
                  {DURATION_OPTIONS.map(d => (
                    <option key={d} value={d}>
                      {d} Min
                    </option>
                  ))}
                </select>
              </div>

              {/* 3. Buttons Container */}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                <button
                  className="btn-primary"
                  style={{
                    fontWeight: 'bold',
                    width: '170px',
                    height: '45px',
                    padding: '0 14px',
                    borderWidth: '2px',
                  }}
                  onClick={addQuestion}
                >
                  Add Question
                </button>

                {/* <button
                  className="btn-primary"
                  style={{
                    width: '200px',
                    height: '45px',
                    padding: '0 14px',
                    borderWidth: '2px',
                  }}
                  onClick={() => setAiModalOpen(true)}
                >
                  Generate with AI
                </button> */}
                <AiGenerator onImport={() => { }} />
              </div>

            </div>
          )}
        </div>
      </div>




      <div className="panel-body" style={{ background: '#f8f9fa' }}>
        {local.questions.map((q, idx) => (
          <div key={idx} className="dashboard-card" style={{ marginBottom: '0.5rem', height: 'auto', overflow: 'visible' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', position: 'relative' }}>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                {/* <span className="badge" style={{ background: '#eee', color: '#555' }}>Question {idx + 1}</span> */}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '-20px' }}>
                <div className="text-base" style={{ fontSize: '11px', color: '#888', fontWeight: 700 }}>
                  Points:
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '2px 4px',
                  background: '#fff',
                  width: 'fit-content'
                }}>
                  <input
                    type="number"
                    step="0.5"
                    style={{
                      border: 'none',
                      width: '35px',
                      fontWeight: 700,
                      fontSize: '12px',
                      textAlign: 'center',
                      color: 'var(--brand-primary)',
                      background: 'transparent',
                      outline: 'none'
                    }}
                    value={q.weight}
                    onChange={e => updateQ(idx, 'weight', parseFloat(e.target.value))}
                    title="Edit Question Points"
                  />
                </div>


                {/* 3-Dot Menu */}
                <div style={{ position: 'relative' }}>
                  <button
                    style={{ fontWeight: 'bold', border: 'none', background: 'none' }}
                    className="btn-icon"
                    onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === idx ? null : idx); }}
                  >
                    <Icons.More />
                  </button>

                  {activeDropdown === idx && (
                 <div className="context-menu" style={{
    position: 'absolute', 
    right: 0, 
    top: '100%',
    background: 'white', 
    border: '1px solid var(--ui-border)',
    borderRadius: '0.5rem', 
    boxShadow: '0 4px 12px rgba(246, 240, 240, 0.1)',
    zIndex: 20, 
    minWidth: '180px', 
    overflow: 'hidden',
    fontSize: '11px',
    padding: '4px 0'
}}>
    {/* Common style for all items  */}
    {[
        { label: 'Edit', icon: <Icons.Edit />, onClick: () => { setEditQ(JSON.parse(JSON.stringify(q))); setEditIdx(idx); } },
        { label: 'More Like This', icon: <Icons.Copy />, onClick: () => handleQuestionUpdateFromAi('more like this', idx, q.q, q?.topics || []) },
        { label: 'Replace', icon: <Replace />, onClick: () => handleQuestionUpdateFromAi('Replace', idx, q.q, q?.topics || []) },
        { label: 'Delete', icon: <Icons.Trash />, onClick: () => { 
            const n = [...local.questions]; 
            n.splice(idx, 1); 
            setLocal({ ...local, questions: n }); 
        }, isDanger: true }
    ].map((item, i) => (
        <button 
            key={i}
            className={`context-menu-item ${item.isDanger ? 'danger' : ''}`}
            onClick={() => { item.onClick(); setActiveDropdown(null); }}
            style={{
                display: 'flex',      
                alignItems: 'center',   
                gap: '8px',            
                width: '100%',
                border: 'none',
                background: 'transparent',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '11px',
                textAlign: 'left',
                color: item.isDanger ? '#d93025' : '#212529'
            }}
        >
        
            <span style={{ 
                display: 'inline-flex', 
                fontSize: '14px', 
                width: '16px', 
                justifyContent: 'center' 
            }}>
                {item.icon}
            </span>
            {item.label}
        </button>
    ))}
</div>
                  )}
                </div>
              </div>
            </div>

            <p className="text-base fw-bold" style={{ margin: '0 0 0.5rem 0', fontSize: '11px', marginTop: '-28px' }}>{q?.q}</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {q.options.map((o, i) => {
                const isCorrect = o === q.correct_answer;
                return (
                  <div
                    key={i}
                    className="text-base fw-normal"
                    style={{
                      fontSize: '11px',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      border: `1px solid ${isCorrect ? 'var(--color-success-text)' : '#eee'}`,
                      background: isCorrect ? 'var(--color-success-bg)' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: isCorrect ? 'var(--color-success-text)' : 'inherit'
                    }}
                  >
                    {isCorrect ? <Icons.Check style={{ color: 'var(--color-success-text)' }} /> : <span style={{ width: '1em' }}></span>}
                    <span style={{ fontWeight: 700, color: '#666' }} >{String.fromCharCode(65 + i)}.</span> {o}
                  </div>
                );
              })}
            </div>
            {q.rationale && (
              <div className="information-card mt-1" style={{ fontSize: '11px', textAlign: 'left', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '6px' }}>
                <strong className="text-base fw-bold" style={{ fontSize: '11px' }}>Rationale:</strong>
                <span className="text-base fw-normal" style={{ fontSize: '11px' }}> {q.rationale}</span>
              </div>
            )} </div>
        ))}
      </div>

      {/* Sticky Action Bar */}
      <div className="sticky-action-bar"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "15px 20px",
          // background: "#fff",
          borderTop: "1px solid #ddd",
          zIndex: 1000
        }}
      >
        <button className="btn btn-outline-primary" onClick={onCancel} title="Discard changes and go back" style={{ marginTop: '-4px', fontSize: '13px' }}>Cancel</button>

        <div style={{ display: 'flex', gap: '10px' }}>

          {local.status === 'Deployed' ? (
            <button className="btn-primary" onClick={() => onSave(local)} title="Save changes and close editor" style={{ marginRight: '12px', marginTop: '-10px' }}>Save & Update</button>
          ) : (
            <>
              <button className="btn btn-outline-primary" onClick={() => onSave({ ...local, status: 'Draft' })} title="Save work without publishing" style={{ fontSize: '13px' }}>Save Draft</button>
              <button className="btn btn btn-primary" onClick={() => onSave({ ...local, status: 'Ready' })} title="Publish to Assessment Library" style={{ marginRight: '10px', fontSize: '13px' }} >Publish Quiz</button>
            </>
          )}
        </div>
      </div>

      {editQ && (
        <Modal title={<div
          style={{
            marginTop: '-5px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginLeft: '20px',
            marginRight: '20px',
            marginTop: '20px',
          }}
        >
          <h4 className='fw-bold' style={{ fontSize: '14px' }}>
            Add Question
          </h4>
          <button
            // onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              lineHeight: 1,
              color: 'inherit',
              padding: 0,

            }}
            onClick={() => {
              setEditQ(null);
              setEditIdx(-1);
            }}
          >
            ×
          </button>
        </div>} onClose={() => { setEditQ(null); setEditIdx(-1); }} footer={
          < div className="d-flex justify-content-between w-100" >
            <button
              className="btn btn-outline-primary"
              // onClick={onCancel}
              title="Discard changes and go back"
              style={{ marginTop: '-4px', fontSize: '13px' }}
              onClick={() => {
                setEditQ(null);
                setEditIdx(-1);
              }}
            >
              Cancel
            </button>

            <button
              className=" btn-primary"
              onClick={saveModalChanges}
            >
              Add
            </button>
          </div >
        }>
          <div className="mb-3" style={{ marginTop: '-15px' }}>
            <label className="text-base fw-bold" style={{ fontSize: '12px' }}>Question Text</label>
            <textarea
              className={`form-control my-custom-textarea ${isValided && !editQ?.q ? "redBorder" : ""}`}
              rows={3}
              value={editQ.q}
              onChange={e => setEditQ({ ...editQ, q: e.target.value })}
              title="Enter question text"
            />
          </div>
          <div className="mb-3">
            <label className="text-base fw-bold" style={{ fontSize: '11px' }}>Options (Select Correct)</label>
            {editQ.options.map((opt, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <input type="radio" name="correctOpt" checked={editQ.correct_answer === opt} onChange={() => setEditQ({ ...editQ, correct_answer: opt })} title="Mark as correct answer" />
                <input className={`form-control ${isValided && !opt ? "redBorder" : ""}`} style={{ padding: '0.4rem' }} value={opt}
                  onChange={(e) => {
                    const newOpts = [...editQ.options];
                    newOpts[i] = e.target.value;
                    setEditQ({ ...editQ, options: newOpts });
                  }}
                  title={`Option ${String.fromCharCode(65 + i)} text`} />
              </div>
            ))}
          </div>
          <div>
            <label className="text-base fw-bold" style={{ fontSize: '11px' }}>Rationale</label>
            <textarea className={`form-control ${isValided && !editQ?.rationale ? "redBorder" : ""}`} rows={2} value={editQ.rationale || ''} onChange={e => setEditQ({ ...editQ, rationale: e.target.value })} title="Explanation for the correct answer" />
          </div>
        </Modal >
      )}

      {/* AI Wizard Modal - Now part of the Editor */}
      {
        aiModalOpen && (
          <AiWizardModal
            onClose={() => setAiModalOpen(false)}
            onImport={handleAiImport}
            difficulty={difficulty}
            setDifficulty={setDifficulty}
            initialTopic={local.title}
          />
        )
      }
    </div >
  );
};

// --- AI Wizard (Modal Version) ---
const AiWizardModal = ({ onClose, onImport, initialTopic, difficulty,
  setDifficulty }) => {
  const [step, setStep, activeDropdown, setActiveDropdown,] = useState('context'); // Start at context
  
  /* --- START: JD FLOW STATE (Copy for Live Project) --- */
  const [topicSource, setTopicSource] = useState('manual'); // 'manual' | 'jd'
  /* --- END: JD FLOW STATE --- */

  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedSubTopics, setSelectedSubTopics] = useState([]);
  const [customTopic, setCustomTopic] = useState('');


  const [topics, setTopics] = useState([])

  const [isOpen, setISOpen] = useState(null); // For future use
  // Quiz Params
  const [count, setCount] = useState(5);

  const [duration, setDuration] = useState('30');

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regeneratingIndex, setRegeneratingIndex] = useState(-1);
  const [feedback, setFeedback] = useState('');

  // Validations
  const isTopicsValid = selectedTopics.length > 0 && selectedTopics.length <= 5;
  const isSubTopicsValid = selectedSubTopics.length <= 12; // Optional, but max 12

  const [subTopics, setSubTopics] = useState([])


  const getSubSubTopics = (topic) => {




    PostCallWithErrorResponse(ApiConfig.GET_SUB_TOPICS, {
      job_description: '',
      topics: selectedTopics
    }).then((res) => {

      if (res?.json?.sub_topics) {

        const allUnrecommendedSubtopics = res?.json.sub_topics.flatMap(
          item => item.unrecommended_subtopics
        );

        setSubTopics(allUnrecommendedSubtopics)
      }

    })

  }

  useEffect(() => {

    if (selectedTopics.length === 0) {
      setSubTopics([])
      return
    }

    const timer = setTimeout(() => {
      getSubSubTopics()
    }, 1000)

    return () => clearTimeout(timer);

  }, [selectedTopics])

  const availableSubTopics = useMemo(() => {
    const allSub = [];
    selectedTopics.forEach(t => {
      const job = JOB_DESCRIPTIONS.find(j => j.title === t);
      if (job) allSub.push(...job.subTopics);
    });
    return [...new Set(allSub)]; // Unique
  }, [selectedTopics]);

  const handleAddCustom = () => {
    if (customTopic.trim()) {
      setTopics(prev => [...prev, customTopic]);
      setCustomTopic('');
    }
  };

  const generate = () => {
    if (!isTopicsValid) return alert("Please select between 1 and 5 main topics.");
    setLoading(true);

    PostCallWithErrorResponse(ApiConfig.GET_QUIZ_QUES, {
      difficulty_level: difficulty,
      job_description: '',
      num_questions: count,
      topics: selectedSubTopics
    }).then((res) => {

      if (res?.json?.quiz?.quiz_questions) {
        setQuestions(res?.json?.quiz?.quiz_questions);

        setStep('review');
      }

      setLoading(false);

    })

    // Pass gathered context
    // const promptContext = `Topics: ${selectedTopics.join(', ')}. SubTopics: ${selectedSubTopics.join(', ')}`;
    // MockService.simulateAi('GET', { topic: promptContext, count }).then(res => {
    //   setQuestions(res.questions);
    //   setLoading(false);
    //   setStep('review');
    // });
  };

  const regenerate = (idx) => {
    MockService.simulateAi('REGENERATE', { feedback, topics: selectedTopics }).then(res => {
      const newQs = [...questions];
      newQs[idx] = res.question;
      setQuestions(newQs);
      setRegeneratingIndex(-1);
      setFeedback('');
    });
  };

  const remove = (idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const handleImport = () => {
    const appQuestions = questions.map(q => ({
      id: crypto.randomUUID(),
      text: q.text,
      type: 'MCQ',
      weight: 1,
      rationale: q.rationale,
      options: q.options,
    })).map(q => {
      q.correctOptionId = q.options[0].id;
      return q;
    });

    const questionsMap = questions?.map((ques) => {
      return {
        ...ques,
        weight: 1,
        type: "MCQ"
      }
    })

    onImport(questionsMap);
  };

  return (
    <Modal title="" onClose={onClose} size="800px" footer={null}>
      <div
      // style={{
      //   padding: '1rem 1.5rem 1.5rem 1.5rem',
      //   maxHeight: '70vh',
      //   overflowY: 'auto',
      //   display: 'flex',
      //   flexDirection: 'column',
      //   position: 'relative',
      // }}
      >

        <div
          style={{
            marginTop: '-5px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h4 className="fw-bold" style={{ fontSize: '14px', fontWeight: '700px' }}>
            AI Question Generator
          </h4>

          {/* <div className="fw-bold" style={{ fontSize: '14px' }}>
  AI Question Generator
</div> */}

          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              lineHeight: 1,
              color: 'inherit',
              padding: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Step content here */}
        {step === 'context' && (
          <div>
            <h6 className="text-base fw-bold" style={{ fontSize: '11px' }}>1. Set Context (Topics from Job Descriptions)</h6>
            <div className="mb-4" style={{ marginTop: '-11px' }}>
              <label className="text-base fw-normal" style={{ fontSize: '11px' }}>Select Main Topics (Max 5)</label>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  marginBottom: '10px',
                }}
              >
                {topics.map((job) => (
                  <button
                    key={job}
                    onClick={() => {
                      if (selectedTopics.includes(job))
                        setSelectedTopics((p) => p.filter((t) => t !== job));
                      else if (selectedTopics.length < 5)
                        setSelectedTopics((p) => [...p, job]);
                    }}
                    className={`btn btn-sm ${selectedTopics.includes(job)
                      ? 'btn-primary'
                      : 'btn-outline-primary'
                      }`}
                    style={{ borderRadius: '20px' }}
                  >
                    {job}
                  </button>
                ))}
              </div>
              <div
                className="input-group"
                style={{ maxWidth: '300px', display: 'flex' }}
              >
                <input
                  className="form-control"
                  style={{
                    fontSize: '0.85rem',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                  placeholder="Add custom topic..."
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                />
                <button
                  className="btn btn-outline-primary"
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: 0,
                  }}
                  onClick={handleAddCustom}
                >
                  <Icons.Plus />
                </button>
              </div>
            </div>

            {selectedTopics.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  padding: '10px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '1rem',
                  maxHeight: '300px',
                  overflow: 'scroll'
                }}
              >
                {subTopics.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => {
                      if (selectedSubTopics.includes(sub))
                        setSelectedSubTopics((p) => p.filter((t) => t !== sub));
                      else if (selectedSubTopics.length < 12)
                        setSelectedSubTopics((p) => [...p, sub]);
                    }}
                    className={`badge ${selectedSubTopics.includes(sub)
                      ? 'badge-primary'
                      : 'badge-secondary'
                      }`}
                    style={{ cursor: 'pointer', border: 'none', fontSize: '0.8rem' }}
                  >
                    {sub}
                  </button>
                ))}
                <div className="text-muted small mt-1" style={{ width: '100%' }}>
                  {selectedSubTopics.length}/12 selected
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'params' && (
          <div>
            <h4 className="fw-bold" style={{ marginTop: '-19px', fontSize: '14px' }}>2. Quiz Parameters</h4>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="spinner-border text-primary mb-2" role="status"></div>
                <p className='text-base fw-normal' fw-normal style={{ fontSize: '11px' }}>
                  AI is crafting your questions based on selected topics...
                </p>
              </div>
            ) : (
              <div
                style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}
              >
                <div style={{ flex: 1 }}>
                  <label className='text-base fw-normal' fw-normal style={{ fontSize: '11px' }}>
                    Number of Questions
                  </label>
                  <select
                    className="form-control"
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                  >
                    <option value="3">3</option>
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="15">15</option>
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <label className='text-base fw-normal' fw-normal style={{ fontSize: '11px' }}>Difficulty Level</label>
                  <select
                    className="form-control"
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                  >
                    <option>Easy</option>
                    <option>Medium</option>
                    <option>Hard</option>
                  </select>
                </div>

                {/* <div style={{ flex: 1 }}>
                  <label className="form-label small fw-bold" style={{ fontSize: '14px' }}>Duration (Minutes)</label>
                  <select
                    className="form-control"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                  >
                    {DURATION_OPTIONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div> */}
              </div>
            )}
          </div>
        )}

        {step === 'review' && (
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <h4 className="fw-bold " style={{ fontSize: '14px' }}>3. Review & Edit</h4>
            {
              console.log(questions, "Amei")
            }
            {questions?.map((q, i) => (
              <div key={i} className="dashboard-card" style={{ marginBottom: '1rem', height: 'auto' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    position: 'relative',
                  }}
                >
                  <span className="badge badge-primary">Question {i + 1}</span>

                  <span
                    className="three-dot-icon"
                    onClick={() => setISOpen(i)}
                  >
                    &#x22EE;
                  </span>

                  {isOpen === i && (
                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: '100%',
                        background: 'white',
                        border: '1px solid var(--ui-border)',
                        borderRadius: '0.5rem',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 20,
                        minWidth: '180px',
                        overflow: 'hidden',
                      }}
                    >
                      {/* <button className="dropdown-item" style={dropdownItemStyle}>
                <Icons.Edit style={{ color: 'var(--brand-primary)' }} /> Edit
              </button>
              <button className="dropdown-item" style={dropdownItemStyle}>
                <Icons.Copy style={{ color: '#8b5cf6' }} /> More Like This
              </button>
              <button className="dropdown-item" style={dropdownItemStyle}>
                <Icons.Replace style={{ color: '#f59e0b' }} /> Replace
              </button> */}
                      <button className="dropdown-item" style={dropdownItemStyle}
                        onClick={() => remove(i)}
                      >
                        <Icons.Trash style={{ color: 'red' }}

                        /> Delete
                      </button>
                    </div>
                  )}
                </div>

                <p style={{ fontWeight: 500, margin: '0 0 0.5rem 0', fontSize: '11px' }} className='text-base fw-bold'>{q?.q}</p>

                <div style={{ fontSize: '11px', color: '#666', marginBottom: '0.5rem' }} className='text-base fw-normal'>
                  {q?.options?.map((o, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem' }}>
                      <span>{String.fromCharCode(65 + idx)}.</span> <span>{o}</span>
                    </div>
                  ))}
                </div>

                {regeneratingIndex === i && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      background: '#f8f9fa',
                      borderRadius: '0.5rem',
                    }}
                  >
                    <input
                      className="form-control"
                      style={{ marginBottom: '0.5rem' }}
                      placeholder="Feedback..."
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                    />
                    <button className="btn btn-primary btn-sm" onClick={() => regenerate(i)}>
                      Regenerate
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}




        {/* Buttons Row */}
        <div
          style={{
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '1rem',
            gap: '0.5rem',
          }}
        >
          <button
            style={{ fontWeight: 'bold', height: '40px', marginTop: '10px', fontSize: '13px' }}
            className="btn btn-outline-primary"
            onClick={() => {
              if (step === 'review') setStep('params');
              else if (step === 'params') setStep('context');
              else onClose();
            }}
          >
            Back
          </button>

          <div>
            {step === 'context' && (
              <button
                style={{ marginTop: '10px', height: '40px', padding: '9.6px 19.2px' }}
                className="btn-primary"
                disabled={!isTopicsValid || !isSubTopicsValid}
                onClick={() => {
                  if (!selectedSubTopics.length) {
                    toastMsg('error', 'Please select at least one sub-topic to proceed.')
                    return
                  }
                  setStep('params')
                }}
              >
                Next
              </button>
            )}

            {/* {step === 'params' && (
              <button className="btn-primary" style={{ marginTop: '15px', padding: '9.6px 19.2px' }}
                onClick={generate}
                disabled={loading}

              >
                {loading ? 'Generating...' : 'Generate '}
              </button>
            )} */}

            {step === 'review' && (
              <button className="btn-primary" onClick={handleImport} style={{ marginTop: '15px' }}>

                Import {questions.length}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

// --- Gradebook ---
const GradebookView = ({ assessment, submissions, antd, onBack, onAdjust }) => {
  const data = [{ studentName: "pankaj", score: '9', total: '10', date: new Date() },
{ studentName: "parvez", score: '9', total: '10', date: new Date() },
{ studentName: "Samir", score: '9', total: '10', date: new Date() },
{ studentName: "amir", score: '9', total: '10', date: new Date() },
{ studentName: "salman", score: '9', total: '10', date: new Date() },
{ studentName: "kureshi", score: '9', total: '10', date: new Date() }

  ]

  // submissions.filter(s => s.assessmentId === assessment.id).map(sub => {
  //   let score = 0, total = 0;
  //   const breakdown = assessment.questions.map(q => {
  //     const correct = sub.responses[q.id] === q.correctOptionId;
  //     const pts = parseFloat(q.weight);
  //     if (correct) score += pts;
  //     total += pts;
  //     return { correct, pts: correct ? pts : 0, w: q.weight };
  //   });
  //   return { ...sub, breakdown, score, total };
  // });

  const columns = [
    {
      title: "Name",
      dataIndex: "studentName",
      key: "studentName",
      align: "center",
      render: (text) => (
        <span title={text}>
          {text}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "submittedAt",
      key: "submittedAt",
      align: "center",
      render: (date) =>
        date ? new Date(date).toLocaleDateString() : "-",
    },
    {
      title: "Score",
      key: "score",
      align: "center",
      render: (_, record) => (
        <span
          style={{
            fontWeight: 700,
            color: "var(--brand-primary)",
          }}
        >
          {record.score}/{record.total}
        </span>
      ),
    },
  ];

  return (
    <div className="content-area" style={{ paddingBottom: '80px' }}>
      <div className="panel-header" style={{ textAlign: 'center' }}>
        <div>
          <h4 className='.text-lg' style={{ margin: 0, fontSize: '14px' }}>Leaderboard: {assessment.title}</h4>
          <small className='text-base' style={{ color: '#666', fontSize: '11px' }}>Company: {assessment.groupCode}</small>
        </div>
        <button className="btn-primary" onClick={onAdjust} title="Modify question points">
          Modify Points
        </button>
      </div>

      <div className="panel-body">
        <Table
          size="large"
          columns={columns}
          dataSource={data}
          // onChange={handleChange}
          // pagination={false}
          className="grouplisttable overFlowTable"
        // tableLayout="fixed"

        // sticky={{ offsetHeader: 0 }}
        // rowSelection={true}
        />
        {/* <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee' }}>
              <th style={{ padding: '8px' }}>Name</th>
              <th style={{ padding: '8px' }}>Date</th>
              <th style={{ padding: '8px' }}>Score</th>
            </tr>
          </thead>

          <tbody style={{ verticalAlign: 'top' }}>
            {data.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '8px' }} title={r.studentName}>
                  {r.studentName}
                </td>
                <td style={{ padding: '8px' }}>{new Date(r.submittedAt).toLocaleDateString()}</td>
                <td style={{ padding: '8px', fontWeight: 700, color: 'var(--brand-primary)' }}>
                  {r.score}/{r.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table> */}
      </div>

      <div className="sticky-action-bar" style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100%',
        backgroundColor: '#fff',
        padding: '15px 0',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        textAlign: 'center',
        zIndex: 1000
      }}>
        <button
          className="btn btn-outline-primary"
          style={{ height: '45px', width: '100px', fontWeight: 'bold', marginLeft: '15px', fontSize: '13px' }}
          onClick={onBack}
        >
          Back
        </button>
      </div>
    </div>
  );
};

// --- View: Student Simulator ---
const StudentSimulatorView = ({ assessment, onClose, onSubmit }) => {
  const [responses, setResponses] = useState({});

  return (
    <div className="content-area" >
      <div className="panel-header">
        <div>
          <h4 className='text-lg' style={{ margin: 0, fontSize: '14px' }}>{assessment.title}</h4>
          <small className='text-base' style={{ color: 'var(--ui-text-muted)', fontSize: '11px' }}>
            Mode: Candidate Preview • Company: {assessment.groupCode}
          </small>
        </div>
        <span className="badge badge-primary" style={{ fontSize: '11px', textTransform: 'none' }}>Preview Mode</span>
      </div>

      <div className="panel-body" style={{ background: '#f8f9fa' }}>
        {assessment.questions.map((q, idx) => (
          <div key={q.id} className="dashboard-card" style={{ marginBottom: '1rem', height: 'auto' }}>
            <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
              <span className="badge" style={{ background: '#eee', color: '#555', textTransform: 'none' }}>Question {idx + 1}</span>
              <span className='text-base' style={{ fontSize: '11px', color: '#888' }}>Points: {q.weight}</span>
            </div>
            <p style={{ margin: '0 0 1rem 0', fontWeight: 500, fontSize: '11px' }} className=''>{q?.q}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '11px' }}>
              {q.options.map((opt, i) => (
                <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', border: '1px solid var(--ui-border)', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name={q.id}
                    checked={q?.correct_answer === opt}
                    onChange={() => setResponses({ ...responses, [q.id]: q.correct_answer })}
                    style={{ accentColor: 'var(--brand-primary)' }}
                  />
                  <span style={{ fontWeight: 600, color: 'var(--ui-text-muted)' }}>{String.fromCharCode(65 + i)}.</span>
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>


      <div className="sticky-action-bar" style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        padding: '8px 13px',
        borderTop: '1px solid var(--ui-border)',
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
        zIndex: 1000
      }}>
<button 
  className="btn btn-outline-primary" 
  onClick={onClose}
  style={{ fontSize: '13px' }}
>
  Close Preview
</button>      </div>
    </div>
  );
};
/* ==============================================================================================
   5. MAIN APP CONTROLLER
   ============================================================================================== */

export default function App() {
  const [view, setView] = useState('dashboard');
  const [activeItem, setActiveItem] = useState(null);
  const [toast, setToast] = useState(null);

  // Data State
  const [assessments, setAssessments] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = LYWO_CSS;
    document.head.appendChild(style);

    // Initial Seed Data with Diverse Samples
    const q1 = {
      id: 'q1', text: 'Which pattern best completes the sequence?', type: 'MCQ', weight: 1,
      rationale: 'Pattern recognition is key for problem solving.',
      options: [{ id: 'o1', text: 'Pattern A' }, { id: 'o2', text: 'Pattern B' }, { id: 'o3', text: 'Pattern C' }, { id: 'o4', text: 'Pattern D' }],
      correctOptionId: 'o1'
    };
    const q2 = {
      id: 'q2', text: 'Select the most appropriate response to the client email.', type: 'MCQ', weight: 1.5,
      rationale: 'Communication skills assessment.',
      options: [{ id: 'o5', text: 'Option A (Polite)' }, { id: 'o6', text: 'Option B (Direct)' }, { id: 'o7', text: 'Option C (Casual)' }, { id: 'o8', text: 'Option D (Technical)' }],
      correctOptionId: 'o5'
    };

    // Master Assessments (Drafts/Ready)
    const m1 = {
      id: 'm1', title: 'General Aptitude Test - Level 1', origin: 'Manual', status: 'Ready', repository: 'lywo',
      durationMinutes: 45, questions: [q1, q2],
      updatedAt: new Date('2023-12-05T10:00:00'), updatedBy: 'HR Admin'
    };

    const m2 = {
      id: 'm2', title: 'Senior Frontend Developer Assessment', origin: 'AI-Generated', status: 'Draft', repository: 'lywo',
      durationMinutes: 60, questions: [q1, q2, q1],
      updatedAt: new Date('2023-11-10T14:00:00'), updatedBy: 'AI Bot'
    };

    // Draft 1 (Company - Manual)
    const d1 = {
      id: 'd_new', title: 'Product Manager Case Study', origin: 'Manual', status: 'Draft', repository: 'company',
      durationMinutes: 90, questions: [q1],
      updatedAt: new Date('2023-12-01T09:30:00'), updatedBy: 'Sarah H.'
    };

    // Master 3 (Company - Bulk)
    const m3 = {
      id: 'm3', title: 'Workplace Safety & Compliance', origin: 'Bulk Upload', status: 'Ready', repository: 'company',
      durationMinutes: 30, questions: [q2, q2, q1],
      updatedAt: new Date('2023-12-20T16:45:00'), updatedBy: 'Compliance Team'
    };

    // Deployed Assessments (In Use) with "Position" field
    const dep1 = { ...m1, id: 'dep1', title: 'Eng. Batch 2024 - Aptitude', status: 'Deployed', groupCode: 'Tesla-Engineering', position: 'Junior Engineer', accessCode: 'APT-001', createdAt: new Date('2023-12-01') };
    const dep2 = { ...m2, id: 'dep2', title: 'Frontend Team - Q1 Skills Check', status: 'Deployed', groupCode: 'Tesla-Frontend', position: 'Senior React Dev', accessCode: 'RCT-002', createdAt: new Date('2023-12-10') };
    const dep3 = { ...m3, id: 'dep3', title: 'Factory Staff - Safety Cert', status: 'Deployed', groupCode: 'Tesla-Factory', position: 'Safety Officer', accessCode: 'SFT-101', createdAt: new Date('2023-12-15') };
    const dep4 = { ...d1, id: 'dep4', title: 'Leadership Evaluation - Managers', status: 'Deployed', groupCode: 'Tesla-Exec', position: 'Team Lead', accessCode: 'MGR-500', createdAt: new Date('2023-12-28') };

    setAssessments([m1, m2, d1, m3, dep1, dep2, dep3, dep4]);
    setSubmissions([
      { studentId: 's1', assessmentId: 'dep1', studentName: 'John Doe', submittedAt: new Date(), responses: { 'q1': 'o1', 'q2': 'o5' } },
      { studentId: 's2', assessmentId: 'dep2', studentName: 'Jane Smith', submittedAt: new Date(), responses: { 'q1': 'o1', 'q2': 'o5' } }
    ]);

    return () => {
      document.head.removeChild(style);
    };

  }, []);

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const handleEdit = (a) => {
    setActiveItem({
      "id": "6ce4a16b-fffa-4d61-b963-79bd0c4350e6",
      "title": "Untitled Quiz",
      "origin": "Manual",
      "status": "Draft",
      "repository": "company",
      "durationMinutes": 30,
      "questions": [
        {
          "q": "In React, what is the primary purpose of PropTypes?",
          "options": [
            "To validate data types of component props",
            "To manage component lifecycle events",
            "To handle asynchronous API calls",
            "To define CSS styling rules"
          ],
          "correct_answer": "To validate data types of component props",
          "rationale": "PropTypes is specifically designed for runtime type checking of props in React components, ensuring data integrity.",
          "topics": [
            "PropTypes for Type Checking"
          ],
          "weight": 1,
          "type": "MCQ"
        },
        {
          "q": "Which JSX syntax correctly renders a component with conditional styling?",
          "options": [
            "<div className={isActive ? 'active' : 'inactive'}>Content</div>",
            "<div style={if(isActive){'active'}else{'inactive'}}>Content</div>",
            "<div class={isActive ? 'active' : 'inactive'}>Content</div>",
            "<div css={isActive ? 'active' : 'inactive'}>Content</div>"
          ],
          "correct_answer": "<div className={isActive ? 'active' : 'inactive'}>Content</div>",
          "rationale": "JSX uses className for CSS classes and requires JavaScript expressions in curly braces for dynamic values.",
          "topics": [
            "JSX Syntax and Usage"
          ],
          "weight": 1,
          "type": "MCQ"
        },
        {
          "q": "The __________ hook is used to read and subscribe to context values in functional components.",
          "options": [
            "useContext",
            "useContextState",
            "useContextValue",
            "useContextProvider"
          ],
          "correct_answer": "useContext",
          "rationale": "useContext is the standard React hook for accessing context values without prop drilling.",
          "topics": [
            "Context API for Global State"
          ],
          "weight": 1,
          "type": "MCQ"
        },
        {
          "q": "What does the useState hook return?",
          "options": [
            "Current state value and updater function",
            "Previous state snapshot and effect cleaner",
            "Context consumer and provider pair",
            "Memoized callback and dependencies array"
          ],
          "correct_answer": "Current state value and updater function",
          "rationale": "useState returns an array with two elements: the current state value and a function to update it.",
          "topics": [
            "State Management with Hooks"
          ],
          "weight": 1,
          "type": "MCQ"
        },
        {
          "q": "When should you use Context API instead of useState for state management?",
          "options": [
            "When sharing state across multiple nested components",
            "When handling form input validations",
            "When managing component-specific UI state",
            "When optimizing rendering performance"
          ],
          "correct_answer": "When sharing state across multiple nested components",
          "rationale": "Context API solves prop drilling by providing global state access, while useState is best for local component state.",
          "topics": [
            "Context API for Global State",
            "State Management with Hooks"
          ],
          "weight": 1,
          "type": "MCQ"
        }
      ],
      "updatedAt": "2026-01-30T06:31:40.823Z",
      "updatedBy": "User"
    }); setView('editor');
  };

  const handleSave = (updated) => {
    // Logic updated to respect the status passed from Editor (Draft vs Master)
    // setAssessments(prev => prev.map(a => a.id === updated.id ? { ...updated, updatedAt: new Date(), updatedBy: 'Current User' } : a));

    console.log(updated);

    let payload = {
      "QuizId": 0,
      "quizName": updated?.title,
      "difficultyLevel": "Beginner",
      "duration": updated?.durationMinutes,
      "Status": updated?.status,
      questions: updated?.questions
    }

    PostCallWithErrorResponse(ApiConfig.CREATE_UPDATE_QUIZ, payload)

    let msg = 'Saved';
    // if (updated.status === 'Deployed') msg = 'Recalculated';
    // else if (updated.status === 'Draft') msg = 'Saved as Draft';
    // else if (updated.status === 'Ready') msg = 'Published';

    notify(msg);
    setView(updated.status === 'Deployed' ? 'gradebook' : 'dashboard');
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete?')) { setAssessments(prev => prev.filter(a => a.id !== id)); notify('Deleted'); }
  };
  const handleNew = () => {
    // DIRECTLY OPEN EDITOR
    const newItem = {
      "id": "6ce4a16b-fffa-4d61-b963-79bd0c4350e6",
      "title": "Untitled Quiz",
      "origin": "Manual",
      "status": "Draft",
      "repository": "company",
      "durationMinutes": 30,
      "questions": [
        {
          "q": "In React, what is the primary purpose of PropTypes?",
          "options": [
            "To validate data types of component props",
            "To manage component lifecycle events",
            "To handle asynchronous API calls",
            "To define CSS styling rules"
          ],
          "correct_answer": "To validate data types of component props",
          "rationale": "PropTypes is specifically designed for runtime type checking of props in React components, ensuring data integrity.",
          "topics": [
            "PropTypes for Type Checking"
          ],
          "weight": 1,
          "type": "MCQ"
        },
        {
          "q": "Which JSX syntax correctly renders a component with conditional styling?",
          "options": [
            "<div className={isActive ? 'active' : 'inactive'}>Content</div>",
            "<div style={if(isActive){'active'}else{'inactive'}}>Content</div>",
            "<div class={isActive ? 'active' : 'inactive'}>Content</div>",
            "<div css={isActive ? 'active' : 'inactive'}>Content</div>"
          ],
          "correct_answer": "<div className={isActive ? 'active' : 'inactive'}>Content</div>",
          "rationale": "JSX uses className for CSS classes and requires JavaScript expressions in curly braces for dynamic values.",
          "topics": [
            "JSX Syntax and Usage"
          ],
          "weight": 1,
          "type": "MCQ"
        },
        {
          "q": "The __________ hook is used to read and subscribe to context values in functional components.",
          "options": [
            "useContext",
            "useContextState",
            "useContextValue",
            "useContextProvider"
          ],
          "correct_answer": "useContext",
          "rationale": "useContext is the standard React hook for accessing context values without prop drilling.",
          "topics": [
            "Context API for Global State"
          ],
          "weight": 1,
          "type": "MCQ"
        },
        {
          "q": "What does the useState hook return?",
          "options": [
            "Current state value and updater function",
            "Previous state snapshot and effect cleaner",
            "Context consumer and provider pair",
            "Memoized callback and dependencies array"
          ],
          "correct_answer": "Current state value and updater function",
          "rationale": "useState returns an array with two elements: the current state value and a function to update it.",
          "topics": [
            "State Management with Hooks"
          ],
          "weight": 1,
          "type": "MCQ"
        },
        {
          "q": "When should you use Context API instead of useState for state management?",
          "options": [
            "When sharing state across multiple nested components",
            "When handling form input validations",
            "When managing component-specific UI state",
            "When optimizing rendering performance"
          ],
          "correct_answer": "When sharing state across multiple nested components",
          "rationale": "Context API solves prop drilling by providing global state access, while useState is best for local component state.",
          "topics": [
            "Context API for Global State",
            "State Management with Hooks"
          ],
          "weight": 1,
          "type": "MCQ"
        }
      ],
      "updatedAt": "2026-01-30T06:31:40.823Z",
      "updatedBy": "User"
    }
    setAssessments(prev => [newItem, ...prev]);
    setActiveItem(newItem);
    setView('editor');
  };

  return (
    <div className="">


      <div className="app-content">
        {view === 'dashboard' && <DashboardView assessments={assessments} onEdit={handleEdit} onDelete={handleDelete} view={view} setView={setView} onNew={handleNew} />}

        {view === 'active' && <DeployedView assessments={assessments} submissions={submissions} onDelete={handleDelete} view={view} setView={setView} onNew={handleNew}
          onAction={(a, type) => {
            setActiveItem({
              "id": "6ce4a16b-fffa-4d61-b963-79bd0c4350e6",
              "title": "Untitled Quiz",
              "origin": "Manual",
              "status": "Draft",
              "repository": "company",
              "durationMinutes": 30,
              "questions": [
                {
                  "q": "In React, what is the primary purpose of PropTypes?",
                  "options": [
                    "To validate data types of component props",
                    "To manage component lifecycle events",
                    "To handle asynchronous API calls",
                    "To define CSS styling rules"
                  ],
                  "correct_answer": "To validate data types of component props",
                  "rationale": "PropTypes is specifically designed for runtime type checking of props in React components, ensuring data integrity.",
                  "topics": [
                    "PropTypes for Type Checking"
                  ],
                  "weight": 1,
                  "type": "MCQ"
                },
                {
                  "q": "Which JSX syntax correctly renders a component with conditional styling?",
                  "options": [
                    "<div className={isActive ? 'active' : 'inactive'}>Content</div>",
                    "<div style={if(isActive){'active'}else{'inactive'}}>Content</div>",
                    "<div class={isActive ? 'active' : 'inactive'}>Content</div>",
                    "<div css={isActive ? 'active' : 'inactive'}>Content</div>"
                  ],
                  "correct_answer": "<div className={isActive ? 'active' : 'inactive'}>Content</div>",
                  "rationale": "JSX uses className for CSS classes and requires JavaScript expressions in curly braces for dynamic values.",
                  "topics": [
                    "JSX Syntax and Usage"
                  ],
                  "weight": 1,
                  "type": "MCQ"
                },
                {
                  "q": "The __________ hook is used to read and subscribe to context values in functional components.",
                  "options": [
                    "useContext",
                    "useContextState",
                    "useContextValue",
                    "useContextProvider"
                  ],
                  "correct_answer": "useContext",
                  "rationale": "useContext is the standard React hook for accessing context values without prop drilling.",
                  "topics": [
                    "Context API for Global State"
                  ],
                  "weight": 1,
                  "type": "MCQ"
                },
                {
                  "q": "What does the useState hook return?",
                  "options": [
                    "Current state value and updater function",
                    "Previous state snapshot and effect cleaner",
                    "Context consumer and provider pair",
                    "Memoized callback and dependencies array"
                  ],
                  "correct_answer": "Current state value and updater function",
                  "rationale": "useState returns an array with two elements: the current state value and a function to update it.",
                  "topics": [
                    "State Management with Hooks"
                  ],
                  "weight": 1,
                  "type": "MCQ"
                },
                {
                  "q": "When should you use Context API instead of useState for state management?",
                  "options": [
                    "When sharing state across multiple nested components",
                    "When handling form input validations",
                    "When managing component-specific UI state",
                    "When optimizing rendering performance"
                  ],
                  "correct_answer": "When sharing state across multiple nested components",
                  "rationale": "Context API solves prop drilling by providing global state access, while useState is best for local component state.",
                  "topics": [
                    "Context API for Global State",
                    "State Management with Hooks"
                  ],
                  "weight": 1,
                  "type": "MCQ"
                }
              ],
              "updatedAt": "2026-01-30T06:31:40.823Z",
              "updatedBy": "User"
            }); setView(type === 'check' ? 'student' : type === 'adjust' ? 'editor' : 'gradebook');
          }} />}

        {view === 'editor' && <EditorView assessment={activeItem} onSave={handleSave} onCancel={() => setView(activeItem.status === 'Deployed' ? 'active' : 'dashboard')} />}

        {view === 'gradebook' && <GradebookView assessment={activeItem} submissions={submissions} onBack={() => setView('active')} onAdjust={() => setView('editor')} />}

        {/* Student Simulator: Close on Left (Back) */}
        {view === 'student' && activeItem && (
          <StudentSimulatorView
            assessment={activeItem}
            onClose={() => setView('active')}
            onSubmit={() => { setView('active'); notify('Submission Received'); }}
          />
        )}
      </div>

      {toast && <Toast msg={toast} />}
    </div>
  );
}

